import { useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { createRealtimeManager, type RealtimeBinding, type RealtimeManager } from '../lib/realtime-core.js';
import { orderKeys } from './useOrders';
import { inventoryKeys } from './useInventory';
import { useUIStore } from '../stores/uiStore';
import { useRealtimeStatus } from '../stores/realtimeStatusStore';
import { config } from '../config';

interface RealtimeConfig {
  onOrderUpdate?: (order: unknown) => void;
  onInventoryUpdate?: (product: unknown) => void;
  onDeliveryUpdate?: (delivery: unknown) => void;
  showNotifications?: boolean;
}

type Payload = { eventType: string; new: unknown; old: unknown };

const SHELL_TABLES: RealtimeBinding[] = [
  { table: 'orders' },
  { table: 'products' },
  { table: 'deliveries' },
  { table: 'inventory_lots' },
];

const warn = (msg: string, err?: unknown) => {
  if (import.meta.env.DEV) console.warn(`[realtime] ${msg}`, err ?? '');
};

/**
 * Main realtime hook (mounted once by the app shell). Subscribes once per mount with a stable
 * effect (handlers are read through a ref, so re-renders never re-subscribe), never throws, and
 * reports 'connecting' | 'live' | 'unavailable' to useRealtimeStatus. When realtime cannot connect
 * it retries with capped exponential backoff; pages keep working from their normal queries.
 */
export function useRealtime(configOptions: RealtimeConfig = {}) {
  const queryClient = useQueryClient();
  const optsRef = useRef(configOptions);
  optsRef.current = configOptions;
  const managerRef = useRef<RealtimeManager | null>(null);

  const handleEvent = useCallback(
    (table: string, payload: Payload) => {
      const opts = optsRef.current;
      const showNotifications = opts.showNotifications ?? true;
      const addNotification = useUIStore.getState().addNotification;
      if (table === 'orders') {
        queryClient.invalidateQueries({ queryKey: orderKeys.lists() });
        if (payload.eventType === 'UPDATE' && payload.new) {
          const order = payload.new as { id: string; order_number: string; status: string };
          queryClient.invalidateQueries({ queryKey: orderKeys.detail(order.id) });
          opts.onOrderUpdate?.(payload.new);
          if (showNotifications) {
            addNotification({
              type: 'info',
              title: 'Order Updated',
              message: `Order ${order.order_number} status changed to ${order.status}`,
            });
          }
        }
        if (payload.eventType === 'INSERT' && showNotifications) {
          const order = payload.new as { order_number: string };
          addNotification({ type: 'info', title: 'New Order', message: `New order ${order.order_number} has been created` });
        }
      } else if (table === 'products') {
        queryClient.invalidateQueries({ queryKey: inventoryKeys.lists() });
        if (payload.eventType === 'UPDATE' && payload.new) {
          const product = payload.new as { id: string; name: string; current_stock: number; min_stock: number };
          queryClient.invalidateQueries({ queryKey: inventoryKeys.detail(product.id) });
          opts.onInventoryUpdate?.(payload.new);
          if (product.current_stock <= product.min_stock && showNotifications) {
            addNotification({ type: 'warning', title: 'Low Stock Alert', message: `${product.name} is running low on stock` });
          }
        }
      } else if (table === 'deliveries') {
        queryClient.invalidateQueries({ queryKey: ['deliveries'] });
        if (payload.eventType === 'UPDATE' && payload.new) {
          opts.onDeliveryUpdate?.(payload.new);
          if (showNotifications) {
            const delivery = payload.new as { delivery_number: string; status: string };
            addNotification({
              type: 'info',
              title: 'Delivery Updated',
              message: `Delivery ${delivery.delivery_number} status changed to ${delivery.status}`,
            });
          }
        }
      } else if (table === 'inventory_lots') {
        queryClient.invalidateQueries({ queryKey: inventoryKeys.lots() });
        if (payload.eventType === 'UPDATE' && payload.new) {
          const lot = payload.new as { product_id: string };
          queryClient.invalidateQueries({ queryKey: inventoryKeys.lotsByProduct(lot.product_id) });
        }
      }
    },
    [queryClient],
  );

  useEffect(() => {
    if (!config.features.realtimeUpdates) {
      useRealtimeStatus.setState({ status: 'off', attempt: 0, retryInMs: null, error: null });
      return undefined;
    }
    const manager = createRealtimeManager({
      client: supabase,
      name: 'app-live',
      bindings: SHELL_TABLES,
      onEvent: handleEvent,
      onStatus: (s) => useRealtimeStatus.setState(s),
      log: warn,
    });
    managerRef.current = manager;
    manager.start();
    return () => {
      manager.stop();
      if (managerRef.current === manager) managerRef.current = null;
    };
  }, [handleEvent]);

  const reconnect = useCallback(() => {
    const m = managerRef.current;
    if (!m) return;
    m.stop();
    m.start();
  }, []);
  const disconnect = useCallback(async () => {
    managerRef.current?.stop();
  }, []);

  return { reconnect, disconnect };
}

/** Subscribe a component to a few filtered tables for its lifetime; never throws, retries quietly. */
function useScopedRealtime(name: string, bindings: RealtimeBinding[] | null, onEvent: (table: string, payload: Payload) => void) {
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;
  const key = bindings ? JSON.stringify(bindings) : '';
  useEffect(() => {
    if (!key || !config.features.realtimeUpdates) return undefined;
    const manager = createRealtimeManager({
      client: supabase,
      name,
      bindings: JSON.parse(key) as RealtimeBinding[],
      onEvent: (t, p) => onEventRef.current(t, p as Payload),
      log: warn,
    });
    manager.start();
    return () => manager.stop();
  }, [name, key]);
}

// Hook for subscribing to specific order updates
export function useOrderRealtime(orderId: string, onUpdate?: (order: unknown) => void) {
  const queryClient = useQueryClient();
  useScopedRealtime(
    'order',
    orderId
      ? [
          { table: 'orders', filter: `id=eq.${orderId}` },
          { table: 'order_items', filter: `order_id=eq.${orderId}` },
        ]
      : null,
    (table, payload) => {
      queryClient.invalidateQueries({ queryKey: orderKeys.detail(orderId) });
      if (table === 'orders' && payload.new) onUpdate?.(payload.new);
    },
  );
}

// Hook for subscribing to specific product updates
export function useProductRealtime(productId: string, onUpdate?: (product: unknown) => void) {
  const queryClient = useQueryClient();
  useScopedRealtime(
    'product',
    productId
      ? [
          { table: 'products', filter: `id=eq.${productId}` },
          { table: 'inventory_lots', filter: `product_id=eq.${productId}` },
        ]
      : null,
    (table, payload) => {
      if (table === 'products') {
        queryClient.invalidateQueries({ queryKey: inventoryKeys.detail(productId) });
        if (payload.new) onUpdate?.(payload.new);
      } else {
        queryClient.invalidateQueries({ queryKey: inventoryKeys.lotsByProduct(productId) });
        queryClient.invalidateQueries({ queryKey: inventoryKeys.detail(productId) });
      }
    },
  );
}

// Hook for presence (who's online)
let presenceSeq = 0;
export function usePresence(roomId: string = 'warehouse') {
  const channelRef = useRef<RealtimeChannel | null>(null);
  const { user } = useAuthStore();

  useEffect(() => {
    if (!user) return;

    // A unique topic per mount: supabase-js returns a still-registered channel for a reused topic,
    // and adding callbacks to it after subscribe() throws.
    try {
      channelRef.current = supabase.channel(`${roomId}:${++presenceSeq}`, {
        config: {
          presence: {
            key: user.id,
          },
        },
      });

      channelRef.current
        .on('presence', { event: 'sync' }, () => {
          const state = channelRef.current?.presenceState();
          console.log('Presence state:', state);
        })
        .on('presence', { event: 'join' }, ({ key, newPresences }) => {
          console.log('User joined:', key, newPresences);
        })
        .on('presence', { event: 'leave' }, ({ key, leftPresences }) => {
          console.log('User left:', key, leftPresences);
        })
        .subscribe(async (status) => {
          if (status === 'SUBSCRIBED') {
            await channelRef.current?.track({
              user_id: user.id,
              user_name: user.fullName,
              online_at: new Date().toISOString(),
            });
          }
        });
    } catch (e) {
      warn('presence channel failed', e);
    }

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current).catch(() => undefined);
        channelRef.current = null;
      }
    };
  }, [roomId, user]);
}

// Import auth store for presence
import { useAuthStore } from '../stores/authStore';

export default {
  useRealtime,
  useOrderRealtime,
  useProductRealtime,
  usePresence,
};
